import { useState, cloneElement, ReactElement } from 'react';
import Children from 'react-children-utilities';
import {
  Box,
  UnorderedList,
  chakra,
  useColorModeValue
} from '@chakra-ui/react';
import { normalizeString } from '@utils';

type CodeBlockTabsProps = {
  options: string[];
  children: ReactElement[];
};

// TODO: refactor to use <Tabs /> from Chakra UI.
export function CodeBlockTabs({ options, children }: CodeBlockTabsProps) {
  const activeBg = '#282C34';
  const inactiveBg = useColorModeValue('gray.100', '#18191C');
  const hoverBg = useColorModeValue('gray.50', '#111214');

  const activeColor = 'white';
  const inactiveColor = useColorModeValue('gray.800', 'gray.300');

  const borderColor = useColorModeValue(
    'rgba(0,0,0,0.1)',
    'rgba(255,255,255,0.05)'
  );

  const [activeTab, setActiveTab] = useState(0);

  const activeTabSlug = normalizeString.toKebabCase(options[activeTab]);
  const tabContent = children[activeTab];

  return (
    <Box my={6} mx="auto">
      <UnorderedList
        role="tablist"
        display="flex"
        flexWrap="wrap"
        listStyleType="none"
        m={0}
        position="relative"
        zIndex={1}
        mb="-1px"
      >
        {options.map((title: string, index: number) => {
          const isActive = index === activeTab;
          const isFirst = index === 0;
          const isLast = index === options.length - 1;

          return (
            <li key={title} role="presentation">
              <chakra.button
                id={`tab-${normalizeString.toKebabCase(title)}`}
                type="button"
                role="tab"
                py={2}
                px={4}
                bg={isActive ? activeBg : inactiveBg}
                borderTopLeftRadius={isFirst ? 'md' : '0'}
                borderTopRightRadius={isLast ? 'md' : '0'}
                color={isActive ? activeColor : inactiveColor}
                borderLeft={isFirst ? '1px solid' : 'none'}
                borderRight="1px solid"
                borderTop="1px solid"
                borderBottom="1px solid"
                borderColor="var(--borderColor)"
                borderBottomColor={isActive ? activeBg : 'var(--borderColor)'}
                pointerEvents={isActive ? 'none' : 'auto'}
                textStyle="sm-medium"
                aria-controls={normalizeString.toKebabCase(title)}
                aria-selected={isActive ? 'true' : 'false'}
                onClick={setActiveTab.bind(null, index)}
                transition="background 0.2s"
                _hover={{
                  bg: !isActive && hoverBg
                }}
              >
                {title}
              </chakra.button>
            </li>
          );
        })}
      </UnorderedList>
      {Children.map(children, (child, index) => {
        const tabSlug = normalizeString.toKebabCase(options[index]);
        const isActive = index === activeTab;

        return cloneElement(child, {
          containerProps: {
            id: tabSlug,
            role: 'tabpanel',
            'aria-labelledby': `tab-${tabSlug}`,
            display: isActive ? 'block' : 'none',
            mt: 0
          },
          className: 'rounded-tl-none'
        });
      })}
    </Box>
  );
}
